import { create } from 'zustand'
import apiHelper from '@/network/api'
import {
  KeywordGroupPost,
  KeywordGroupMetric,
  KeywordGroupPagination,
  KeywordGroupDetailResponse
} from '@/types/KeywordGroup'

interface KeywordAnalyticsState {
  groupId: string
  groupName: string
  logicOperator: string
  keywords: string[]
  posts: KeywordGroupPost[]
  metrics: KeywordGroupMetric[]
  pagination: KeywordGroupPagination | null
  page: number
  hasMore: boolean
  isInitialLoading: boolean
  isPaginationLoading: boolean
  error: string | null
  // Computed getter for backwards compatibility
  keyword: string
  fetchKeywordAnalytics: (id: string, page?: number, metricDays?: number, containsKeyword?: boolean, excludeWords?: string, hideReplies?: boolean, resultLabel?: string) => Promise<void>
  fetchKeywordDetail: (id: string) => Promise<void>
  resetAnalytics: () => void
}

const useKeywordAnalyticsStore = create<KeywordAnalyticsState>((set, get) => ({
  groupId: '',
  groupName: '',
  logicOperator: '',
  keywords: [],
  posts: [],
  metrics: [],
  pagination: null,
  page: 1,
  hasMore: true,
  isInitialLoading: false,
  isPaginationLoading: false,
  error: null,
  
  // Computed getter for backwards compatibility
  get keyword() {
    return get().groupName
  },

  fetchKeywordAnalytics: async (id: string, page: number = 1, metricDays: number = 3, containsKeyword?: boolean, excludeWords?: string, hideReplies?: boolean, resultLabel?: string) => {
    try {
      // Set loading state based on whether it's initial load or pagination
      set({ 
        isInitialLoading: page === 1,
        isPaginationLoading: page > 1,
        error: null 
      })

      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        metricDays: metricDays.toString()
      })
      
      if (containsKeyword !== undefined) {
        params.append('containsKeyword', containsKeyword.toString())
      }
      
      if (excludeWords) {
        params.append('excludeWords', excludeWords)
      }

      if (hideReplies) {
        params.append('hideReplies', 'true')
      }

      if (resultLabel) {
        params.append('resultLabel', resultLabel)
      }

      const response = await apiHelper(null).get(`keyword-groups/analytics/${id}?${params.toString()}`)
      
      if (response.status === 200) {
        const data: KeywordGroupDetailResponse = response.data
        const newPosts = data.posts ?? []
        set((state) => ({ 
          groupId: data.groupId,
          groupName: data.groupName,
          logicOperator: data.logicOperator,
          keywords: data.keywords,
          // Append new posts to existing ones if page > 1
          posts: page > 1 ? [...state.posts, ...newPosts] : newPosts,
          metrics: data.metrics,
          pagination: data.pagination,
          page,
          hasMore: newPosts.length > 0,
          isInitialLoading: false,
          isPaginationLoading: false
        }))
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch keyword analytics',
        isInitialLoading: false,
        isPaginationLoading: false
      })
      console.error('Error fetching keyword analytics:', error)
    }
  },

  fetchKeywordDetail: async (id: string) => {
    // This is a simplified version for the notification page
    // that just fetches basic group info without analytics
    try {
      set({ isInitialLoading: true, error: null })
      
      const response = await apiHelper(null).get(`keyword-groups/${id}`)
      
      if (response.status === 200) {
        const data = response.data
        set({ 
          groupId: data.id || data.groupId || id,
          groupName: data.name || data.groupName || '',
          keywords: data.keywords || [],
          isInitialLoading: false
        })
      }
    } catch (error) {
      set({ 
        error: 'Failed to fetch keyword details',
        isInitialLoading: false
      })
      console.error('Error fetching keyword details:', error)
    }
  },

  resetAnalytics: () => {
    set({
      groupId: '',
      groupName: '',
      logicOperator: '',
      keywords: [],
      posts: [],
      metrics: [],
      pagination: null,
      page: 1,
      hasMore: true,
      isInitialLoading: false,
      isPaginationLoading: false,
      error: null
    })
  }
}))

export default useKeywordAnalyticsStore
